import { formatEpoch, safeMessage, tidy } from "../shared/safe";
import { emptySupplied, toSupplied, type SuppliedDraft, type SuppliedKey } from "./supplied/model";

// Pure parsing and wording for the Site kit screen (kits composed from ONE client's approved records, built, previewed and released
// LOCALLY: lane C's routes; real examples in tests/contract/fixtures/media-dto.json). Everything from the server is untrusted text.
// A build is a DRAFT. A release only records, in the local database, that a build is the site's current release: it publishes nothing.

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const nonEmpty = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const int = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);
const strList = (v: unknown, max = 400): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").map((x) => safeMessage(x, max)) : []);
const iso = (v: unknown): { text: string; iso: string } | null => (typeof v === "string" && !Number.isNaN(Date.parse(v)) ? formatEpoch(Date.parse(v) / 1000) : null);

// ---- kits --------------------------------------------------------------------------------------------------------------------------

export interface KitView {
  kitId: string;
  title: string;
  family: string;
  version: string;
  composable: boolean;
  pages: { routeId: string; title: string; pathPattern: string }[];
  /** Plain-words labels of the data the kit needs (from requiredData), never invented by this page. */
  needs: string[];
  /** The kit's catalogue entry (Template Studio), when the server sent one. `supplied` is what the owner must provide in the compose request. */
  catalogue: { name: string; summary: string; supplied: { key: string; description: string; required: boolean; example: unknown } | null } | null;
}
function needLabels(rd: unknown): string[] {
  const out: string[] = [];
  for (const r of Array.isArray(rd) ? rd : []) {
    if (!isObject(r)) continue;
    const label = nonEmpty(r.label);
    if (label) out.push(r.required === false || (r.kind === "collection" && r.minItems === 0) ? `${tidy(label, 80)} (optional)` : tidy(label, 80));
  }
  return out;
}
function readCatalogue(v: unknown): KitView["catalogue"] {
  if (!isObject(v) || !nonEmpty(v.name)) return null;
  const s = isObject(v.supplied) && nonEmpty(v.supplied.key) ? { key: tidy(v.supplied.key, 40), description: safeMessage(v.supplied.description, 600), required: v.supplied.required === true, example: v.supplied.example } : null;
  return { name: tidy(v.name, 80), summary: nonEmpty(v.summary) ? safeMessage(v.summary, 300) : "", supplied: s };
}
export function parseKits(body: unknown): { kits: KitView[]; composesFrom: Record<string, string> } | null {
  if (!isObject(body) || !Array.isArray(body.kits)) return null;
  const kits = body.kits.filter(isObject).flatMap((k): KitView[] => {
    const kitId = nonEmpty(k.kitId);
    if (!kitId) return [];
    return [
      {
        kitId,
        title: nonEmpty(k.title) ? tidy(k.title, 120) : kitId,
        family: nonEmpty(k.family) ? tidy(k.family, 40) : "",
        version: nonEmpty(k.version) ? tidy(k.version, 40) : "",
        composable: k.composable === true,
        pages: (Array.isArray(k.pages) ? k.pages : []).filter(isObject).map((p) => ({ routeId: tidy(p.routeId, 60), title: tidy(p.title, 80), pathPattern: tidy(p.pathPattern, 120) })),
        needs: needLabels(k.requiredData),
        catalogue: readCatalogue(k.catalogue),
      },
    ];
  });
  const cf = isObject(body.composesFrom) ? Object.fromEntries(Object.entries(body.composesFrom).filter(([, v]) => typeof v === "string").map(([k, v]) => [tidy(k, 30), safeMessage(v, 300)])) : {};
  return { kits, composesFrom: cf };
}

// ---- sites -------------------------------------------------------------------------------------------------------------------------

export interface SiteView {
  satelliteId: string | null;
  siteId: string;
  siteKind: "main" | "satellite" | "independent";
  platform: string | null;
  providerSlug: string | null;
  removedAt: string | null;
  kitId: string;
  root: string;
  urlStyle: string;
  latestRevision: number;
  currentReleaseNumber: number;
}
export function readSite(x: unknown): SiteView | null {
  if (!isObject(x) || !nonEmpty(x.siteId)) return null;
  const d = isObject(x.destination) ? x.destination : {};
  return { satelliteId: nonEmpty(x.satelliteId), siteId: x.siteId as string, siteKind: x.siteKind === "independent" || x.siteKind === "satellite" ? x.siteKind : "main", platform: nonEmpty(x.platform), providerSlug: nonEmpty(x.providerSlug), removedAt: nonEmpty(x.removedAt), kitId: nonEmpty(x.kitId) ? tidy(x.kitId, 80) : "", root: nonEmpty(d.root) ? tidy(d.root, 300) : "", urlStyle: nonEmpty(d.urlStyle) ? tidy(d.urlStyle, 30) : "", latestRevision: int(x.latestRevision) ?? 0, currentReleaseNumber: int(x.currentReleaseNumber) ?? 0 };
}
export function parseSites(body: unknown): SiteView[] | null {
  return isObject(body) && Array.isArray(body.sites) ? body.sites.map(readSite).filter((s): s is SiteView => s !== null) : null;
}
export interface SiteDetail {
  site: SiteView;
  latestRevision: { revisionId: string; revision: number; factsRevisionId: string; factsRevisionNow: string; behindFacts: boolean } | null;
}
export function parseSiteDetail(body: unknown): SiteDetail | null {
  if (!isObject(body)) return null;
  const site = readSite(body.site);
  if (!site) return null;
  const r = isObject(body.latestRevision) ? body.latestRevision : null;
  return { site, latestRevision: r && nonEmpty(r.revisionId) ? { revisionId: r.revisionId as string, revision: int(r.revision) ?? 0, factsRevisionId: tidy(r.factsRevisionId, 60), factsRevisionNow: tidy(r.factsRevisionNow, 60), behindFacts: r.behindFacts === true } : null };
}
export function parseSiteReply(body: unknown): { site: SiteView; replayed: boolean } | null {
  if (!isObject(body)) return null;
  const site = readSite(body.site);
  return site ? { site, replayed: body.replayed === true } : null;
}

// ---- revisions ---------------------------------------------------------------------------------------------------------------------

export interface RevisionView {
  revisionId: string;
  revision: number;
  factsRevisionId: string;
  createdBy: string | null;
  createdAt: { text: string; iso: string } | null;
  approvals: { facts: string; content: string; media: string };
}
export function readRevision(x: unknown): RevisionView | null {
  if (!isObject(x) || !nonEmpty(x.revisionId)) return null;
  const refs = isObject(x.references) ? x.references : {};
  const ap = isObject(x.approval) ? x.approval : {};
  return {
    revisionId: x.revisionId as string,
    revision: int(x.revision) ?? 0,
    factsRevisionId: nonEmpty(refs.factsRevisionId) ? tidy(refs.factsRevisionId, 60) : "",
    createdBy: nonEmpty(x.createdBy) ? tidy(x.createdBy, 120) : null,
    createdAt: iso(x.createdAt),
    approvals: { facts: tidy(ap.facts, 30), content: tidy(ap.content, 30), media: tidy(ap.media, 30) },
  };
}
export function parseRevisionList(body: unknown): RevisionView[] | null {
  return isObject(body) && Array.isArray(body.revisions) ? body.revisions.map(readRevision).filter((r): r is RevisionView => r !== null) : null;
}
export interface Composition {
  derived: string[];
  assumptions: string[];
  excluded: { what: string; why: string }[];
  journalCount: number;
  mediaCount: number;
}
export function parseComposeReply(body: unknown): { revision: RevisionView; composition: Composition } | null {
  if (!isObject(body)) return null;
  const revision = readRevision(body.revision);
  const c = isObject(body.composition) ? body.composition : null;
  if (!revision || !c) return null;
  return {
    revision,
    composition: {
      derived: strList(c.derived, 500),
      assumptions: strList(c.assumptions, 500),
      excluded: (Array.isArray(c.excluded) ? c.excluded : []).filter(isObject).map((e) => ({ what: safeMessage(e.what, 300), why: safeMessage(e.why, 300) })),
      journalCount: Array.isArray(c.journalMasterIds) ? c.journalMasterIds.length : 0,
      mediaCount: Array.isArray(c.mediaRevisionIds) ? c.mediaRevisionIds.length : 0,
    },
  };
}

// ---- builds ------------------------------------------------------------------------------------------------------------------------

export interface BuildView {
  buildId: string;
  freshness: { fresh: boolean; reasons: string[] } | null;
  recordId: string;
  siteRevisionId: string;
  createdAt: { text: string; iso: string } | null;
  state: string;
  releaseHash: string;
  baseReleaseNumber: number | null;
  pages: { pageId: string; title: string; path: string; predictedUrl: string; kind: string }[];
  fileCount: number;
  /** Bytes of every built file plus every pinned image: what a provider with a size limit has to accept. */
  totalBytes: number;
  assets: { mediaId: string; revisionId: string; publishedPath: string; mime: string; sha256: string }[];
  /** PLANNED links: what the build intends to contain. Nothing says they were delivered or observed anywhere. */
  plannedLinks: { sourcePageId: string; anchorText: string; href: string; scope: string; linkClass: string; rel: string[] }[];
  diagnostics: { severity: string; code: string; message: string }[];
}
export function readBuild(x: unknown): BuildView | null {
  if (!isObject(x) || !nonEmpty(x.recordId)) return null;
  return {
    buildId: tidy(x.buildId, 128),
    freshness: isObject(x.freshness) ? { fresh: x.freshness.fresh === true, reasons: strList(x.freshness.reasons) } : null,
    recordId: x.recordId as string,
    siteRevisionId: nonEmpty(x.siteRevisionId) ? tidy(x.siteRevisionId, 80) : "",
    createdAt: iso(x.createdAt),
    state: nonEmpty(x.state) ? tidy(x.state, 30) : "draft",
    releaseHash: nonEmpty(x.releaseHash) ? tidy(x.releaseHash, 80) : "",
    baseReleaseNumber: int(x.baseReleaseNumber),
    pages: (Array.isArray(x.pages) ? x.pages : []).filter(isObject).map((p) => ({ pageId: tidy(p.pageId, 80), title: tidy(p.title, 160), path: tidy(p.path, 200), predictedUrl: nonEmpty(p.predictedUrl) ? tidy(p.predictedUrl, 400) : "", kind: nonEmpty(p.kind) ? tidy(p.kind, 30) : "content" })),
    fileCount: Array.isArray(x.files) ? x.files.length : 0,
    totalBytes: [...(Array.isArray(x.files) ? x.files : []), ...(Array.isArray(x.assets) ? x.assets : [])].filter(isObject).reduce((n, f) => n + (typeof f.bytes === "number" ? f.bytes : typeof f.byteSize === "number" ? f.byteSize : 0), 0),
    assets: (Array.isArray(x.assets) ? x.assets : []).filter(isObject).map((a) => ({ mediaId: tidy(a.mediaId, 60), revisionId: tidy(a.revisionId, 80), publishedPath: tidy(a.publishedPath, 300), mime: tidy(a.mime, 40), sha256: tidy(a.sha256, 80) })),
    plannedLinks: (Array.isArray(x.plannedLinks) ? x.plannedLinks : []).filter(isObject).map((l) => ({ sourcePageId: tidy(l.sourcePageId, 80), anchorText: tidy(l.anchorText, 200), href: tidy(l.renderedHref ?? l.requestedHref, 500), scope: tidy(l.scope, 30), linkClass: tidy(l.linkClass, 30), rel: (Array.isArray(l.relTokens) ? l.relTokens : []).filter((t): t is string => typeof t === "string").map((t) => tidy(t, 30)) })),
    diagnostics: (Array.isArray(x.diagnostics) ? x.diagnostics : []).filter(isObject).map((d) => ({ severity: tidy(d.severity, 20), code: tidy(d.code, 60), message: safeMessage(d.message, 400) })),
  };
}
export function parseBuildReply(body: unknown): { build: BuildView; replayed: boolean } | null {
  if (!isObject(body)) return null;
  const build = readBuild(body.build);
  return build ? { build, replayed: body.replayed === true } : null;
}
export function parseBuildList(body: unknown): BuildView[] | null {
  return isObject(body) && Array.isArray(body.builds) ? body.builds.map(readBuild).filter((b): b is BuildView => b !== null) : null;
}

// ---- preview and release -----------------------------------------------------------------------------------------------------------

export interface PreviewView {
  /** The signed link. It is a credential: it expires and reads only this build. */
  url: string;
  overviewPath: string;
  expiresAt: number;
  expiresText: string;
  pages: number;
}
/** Only an http(s) address is used as a link or frame source. */
export function parsePreview(body: unknown): PreviewView | null {
  if (!isObject(body) || !isObject(body.preview)) return null;
  const p = body.preview;
  const url = nonEmpty(p.url);
  const exp = typeof p.expiresAt === "string" ? Date.parse(p.expiresAt) : NaN;
  if (!url || Number.isNaN(exp)) return null;
  try {
    const u = new URL(url);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return { url: u.href, overviewPath: nonEmpty(p.overviewPath) ? tidy(p.overviewPath, 200) : u.pathname, expiresAt: exp, expiresText: new Date(exp).toLocaleString(), pages: int(p.pages) ?? 0 };
  } catch {
    return null;
  }
}
export function remainingText(expiresAt: number, now = Date.now()): string | null {
  const s = Math.floor((expiresAt - now) / 1000);
  if (s <= 0) return null;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export interface ReleaseView {
  releaseId: string;
  releaseNumber: number;
  siteRevision: number;
  buildId: string;
}
export function readRelease(x: unknown): ReleaseView | null {
  if (!isObject(x) || !nonEmpty(x.releaseId)) return null;
  return { releaseId: x.releaseId as string, releaseNumber: int(x.releaseNumber) ?? 0, siteRevision: int(x.siteRevision) ?? 0, buildId: nonEmpty(x.buildId) ? tidy(x.buildId, 80) : "" };
}
export function parseReleaseReply(body: unknown): { release: ReleaseView; replayed: boolean; note: string | null } | null {
  if (!isObject(body)) return null;
  const release = readRelease(body.release);
  return release ? { release, replayed: body.replayed === true, note: nonEmpty(body.note) ? safeMessage(body.note, 500) : null } : null;
}
export function parseReleaseList(body: unknown): ReleaseView[] | null {
  return isObject(body) && Array.isArray(body.releases) ? body.releases.map(readRelease).filter((r): r is ReleaseView => r !== null) : null;
}

// ---- the compose form --------------------------------------------------------------------------------------------------------------

export interface PinChoice {
  mediaId: string;
  revisionId: string;
  /** "hero", "logo" (the site header), VISIT_PHOTO_ROLE (pinned for the Visit page only, no placement), or a service name for { service }. */
  role: string;
}
/** A pinned image the Visit page uses (the storefront photograph); the API receives a pin with no role. */
export const VISIT_PHOTO_ROLE = "@visit-photo";
export interface ComposeDraft {
  mode: "demo" | "live";
  sitewideFooterLink: boolean;
  approveFacts: boolean;
  approveContent: boolean;
  pins: PinChoice[];
  tagline: string;
  summary: string;
  aboutStory: string;
  primaryHex: string;
  accentHex: string;
  country: string;
  /** The entries the owner supplies for kits whose collections are not stored records (directory listings, wiki references, area guides...), as the guided form edits them. */
  supplied: SuppliedDraft;
}
export const emptyCompose = (): ComposeDraft => ({ mode: "demo", sitewideFooterLink: false, approveFacts: false, approveContent: false, pins: [], tagline: "", summary: "", aboutStory: "", primaryHex: "", accentHex: "", country: "", supplied: emptySupplied() });

export const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function composeProblems(d: ComposeDraft): { field: string; message: string }[] {
  const out: { field: string; message: string }[] = [];
  if (!d.approveFacts) out.push({ field: "approveFacts", message: "Tick to confirm the client's current approved facts are what this site should be built from." });
  if (!d.approveContent) out.push({ field: "approveContent", message: "Tick to confirm the approved content (and any copy you write here) may be used on this site." });
  for (const [f, v] of [["primaryHex", d.primaryHex], ["accentHex", d.accentHex]] as const) if (v.trim() !== "" && !HEX_RE.test(v.trim())) out.push({ field: f, message: "Use a colour like #1f4d3a." });
  return out;
}

/** The request body. Only what the person supplied is sent; nothing is invented here. */
export function composeBody(d: ComposeDraft, expectedRevision: number, suppliedKey: SuppliedKey | null = null): Record<string, unknown> {
  const copy: Record<string, unknown> = {};
  if (d.tagline.trim()) copy.tagline = d.tagline.trim();
  if (d.summary.trim()) copy.summary = d.summary.trim();
  const story = d.aboutStory.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (story.length) copy.aboutStory = story;
  const brand: Record<string, string> = {};
  if (d.primaryHex.trim()) brand.primaryHex = d.primaryHex.trim();
  if (d.accentHex.trim()) brand.accentHex = d.accentHex.trim();
  return {
    expectedRevision,
    approvals: { facts: d.approveFacts, content: d.approveContent },
    mode: d.mode,
    ...(d.sitewideFooterLink ? { policy: { sitewideFooterLink: true } } : {}),
    ...(d.pins.length ? { mediaPins: d.pins.map((p) => ({ mediaId: p.mediaId, revisionId: p.revisionId, ...(p.role === VISIT_PHOTO_ROLE ? {} : { role: p.role === "hero" || p.role === "logo" ? p.role : { service: p.role } }) })) } : {}),
    ...(Object.keys(copy).length ? { copy } : {}),
    ...(Object.keys(brand).length ? { brand } : {}),
    ...(d.country.trim() ? { country: d.country.trim() } : {}),
    ...(() => { const sup = suppliedKey ? toSupplied(suppliedKey, d.supplied) : null; return sup ? { supplied: sup } : {}; })(),
  };
}

export const HOSTS_NOTE = 'A "live" site refuses addresses that can never be public (.test, .example and similar) when it is built. Choose "demo" for synthetic clients: demo is the explicit, labelled test mode.';
